import { useCallback, useEffect, useState } from "react";
import { Lock, Zap, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { opsApi, money, pct } from "../lib/api";

/**
 * EST-QUAL-OT downstream — the accepted acceleration option as an overtime allowance.
 *
 * `mode="accept"` is the contract-record control: what was quoted, and the one checkbox that
 * carries it. `mode="allowance"` is the budget line and the burn-down against approved field
 * hours. Nothing here moves the contract value — an allowance becomes contract money through a
 * change order, like everything else.
 */
export default function AccelerationPanel({ projectId, mode = "allowance" }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => opsApi.acceleration(projectId).then(setData).catch(() => {}),
                           [projectId]);
  useEffect(() => { load(); }, [load]);

  const accept = async (next) => {
    setBusy(true);
    try {
      const d = await opsApi.acceptAcceleration(projectId, next);
      setData(d);
      toast.success(next ? "Accepted — the overtime allowance is on the job"
                         : "Not accepted — the allowance has been removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that");
    } finally { setBusy(false); }
  };

  if (!data || (!data.offered && !data.accepted)) return null;
  const a = data.allowance;

  if (mode === "accept") {
    const q = a || data.offer || {};
    return (
      <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/60 p-3"
           data-testid="ops-acceleration-accept">
        <label className="flex items-start gap-2">
          <input type="checkbox" className="mt-0.5" disabled={busy}
                 checked={!!data.accepted} onChange={(e) => accept(e.target.checked)}
                 data-testid="ops-acceleration-accepted" />
          <span className="text-sm text-slate-800">
            Schedule acceleration accepted by customer
            <span className="mt-0.5 block text-xs text-slate-500">
              Quoted as an option at <b>{money(q.amount)}</b> for <b>{q.ot_hours}</b> overtime
              hours. Ticking this carries it to the budget as an overtime allowance — it does not
              change the contract value.
            </span>
          </span>
        </label>
      </div>
    );
  }

  if (!a) return null;
  const tone = a.level === "over" ? "border-rose-300 bg-rose-50"
             : a.level === "warn" ? "border-amber-300 bg-amber-50"
             : "border-slate-200 bg-white";
  return (
    <div className={`overflow-hidden rounded-xl border ${tone}`} data-testid="ops-acceleration-allowance">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2.5">
        <Zap className="h-4 w-4 text-amber-500" />
        <h4 className="text-sm font-semibold text-slate-900">{a.label}</h4>
        <Lock className="h-3 w-3 text-slate-400" title="The hours and amount came from the accepted option" />
        <span className="text-xs text-slate-500" data-testid="ops-acceleration-summary">
          {money(a.amount)} allowance · {a.ot_hours} OT hours at {money(a.ot_premium_rate)}/h ·
          cost {money(a.cost)} · margin {money(a.margin)} ({pct(a.margin_percent)})
        </span>
      </div>
      <div className="grid gap-3 px-4 py-3 text-sm sm:grid-cols-4">
        {[["Overtime used", `${a.ot_hours_used} h`], ["Remaining", `${a.ot_hours_remaining} h`],
          ["Earned", money(a.revenue_earned)], ["Cost incurred", money(a.cost_incurred)]].map(
          ([label, value]) => (
            <div key={label}>
              <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
              <div className="font-semibold text-slate-900">{value}</div>
            </div>
          ))}
      </div>
      <div className="px-4 pb-3">
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
          <div className={`h-full ${a.level === "over" ? "bg-rose-500"
                                  : a.level === "warn" ? "bg-amber-500" : "bg-emerald-500"}`}
               style={{ width: `${Math.min(100, (a.used_percent || 0) * 100)}%` }} />
        </div>
        <p className={`mt-1.5 flex items-center gap-1 text-xs ${
          a.level === "ok" ? "text-slate-500" : "font-medium text-amber-900"}`}
           data-testid="ops-acceleration-message">
          {a.level !== "ok" && <AlertTriangle className="h-3 w-3" />}
          {pct(a.used_percent)} of the allowance used — {a.message}. {a.billing_note}
        </p>
      </div>
    </div>
  );
}
